// Gestur peta untuk kanvas: geser 1 jari / mouse-drag, cubit (pinch) berjangkar di titik tengah,
// ketuk-dua-kali zoom (bisa dimatikan: { ketukDua: false } → ketuk langsung tanpa jeda, dipakai Alun-alun),
// ketuk tunggal → onTap(lx, ly). Kamera dikendalikan lewat api engine (pan / zoomAtPoint);
// loop frame engine membaca kamera tiap frame sehingga tak perlu render manual.
import { useRef } from "react";

export function useMapGestures(engineRef, onTap, { ketukDua = true } = {}) {
  const g = useRef(null);          // state gestur yang sedang berjalan
  const lastTap = useRef(0);       // waktu ketuk terakhir (deteksi ketuk-dua-kali)
  const tapTimer = useRef(null);
  const lastTouch = useRef(0);     // abaikan mouse-event sintetis yang menyusul sentuhan

  const dist = t => Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);
  const mid = t => [(t[0].clientX + t[1].clientX) / 2, (t[0].clientY + t[1].clientY) / 2];
  const sudut = t => Math.atan2(t[1].clientY - t[0].clientY, t[1].clientX - t[0].clientX);
  const local = (el, x, y) => { const r = el.getBoundingClientRect(); return [x - r.left, y - r.top]; };

  function tapAt(el, x, y) {
    const E = engineRef.current;
    if (!E) return;
    const [lx, ly] = local(el, x, y);
    if (!ketukDua) { onTap(lx, ly); return; }
    const now = performance.now();
    if (now - lastTap.current < 300) {           // ketuk dua kali → perbesar 1.8× di titik itu, dianimasikan halus (ease-out, ruang log)
      clearTimeout(tapTimer.current);
      lastTap.current = 0;
      const z0 = E.cam.zoom, lf = Math.log(1.8), t0 = now, ms = 320;
      const tick = t => { const k = Math.min(1, (t - t0) / ms), e = 1 - Math.pow(1 - k, 3); E.zoomAtPoint(z0 * Math.exp(lf * e) / E.cam.zoom, lx, ly); if (k < 1) requestAnimationFrame(tick); };
      requestAnimationFrame(tick);
      return;
    }
    lastTap.current = now;
    tapTimer.current = setTimeout(() => onTap(lx, ly), 260);
  }

  return {
    onTouchStart(e) {
      lastTouch.current = performance.now();
      if (e.touches.length === 1) {
        g.current = { mode: "pan", x: e.touches[0].clientX, y: e.touches[0].clientY, moved: false };
      } else if (e.touches.length === 2) {
        g.current = { mode: "pinch", d: dist(e.touches), m: mid(e.touches), a: sudut(e.touches), moved: true };
      }
    },
    onTouchMove(e) {
      const E = engineRef.current, s = g.current;
      if (!E || !s) return;
      if (s.mode === "pan" && e.touches.length === 1) {
        const dx = e.touches[0].clientX - s.x, dy = e.touches[0].clientY - s.y;
        if (Math.abs(dx) + Math.abs(dy) > 4) s.moved = true;
        if (s.moved) E.pan(dx, dy);
        s.x = e.touches[0].clientX; s.y = e.touches[0].clientY;
      } else if (e.touches.length === 2) {
        if (s.mode !== "pinch") g.current = { mode: "pinch", d: dist(e.touches), m: mid(e.touches), a: sudut(e.touches), moved: true };
        const p = g.current, nd = dist(e.touches), nm = mid(e.touches), na = sudut(e.touches);
        const [lx, ly] = local(e.currentTarget, nm[0], nm[1]);
        if (p.d > 0) E.zoomAtPoint(nd / p.d, lx, ly);
        E.pan(nm[0] - p.m[0], nm[1] - p.m[1]);
        if (E.putar) { let da = na - p.a; if (da > Math.PI) da -= 2 * Math.PI; if (da < -Math.PI) da += 2 * Math.PI; E.putar(da, true); }   // puntir 2 jari = putar kamera
        p.d = nd; p.m = nm; p.a = na;
      }
    },
    onTouchEnd(e) {
      lastTouch.current = performance.now();
      const s = g.current;
      if (!s) return;
      if (e.touches.length === 0) {
        if (s.mode === "pan" && !s.moved) tapAt(e.currentTarget, s.x, s.y);
        g.current = null;
      } else if (e.touches.length === 1) {       // satu jari diangkat setelah cubit → lanjut geser
        g.current = { mode: "pan", x: e.touches[0].clientX, y: e.touches[0].clientY, moved: true };
      }
    },
    onMouseDown(e) {
      if (performance.now() - lastTouch.current < 600) return;
      g.current = { mode: "mouse", x: e.clientX, y: e.clientY, moved: false };
    },
    onMouseMove(e) {
      const E = engineRef.current, s = g.current;
      if (!E || !s || s.mode !== "mouse") return;
      const dx = e.clientX - s.x, dy = e.clientY - s.y;
      if (Math.abs(dx) + Math.abs(dy) > 3) s.moved = true;
      if (s.moved) E.pan(dx, dy);
      s.x = e.clientX; s.y = e.clientY;
    },
    onMouseUp(e) {
      const s = g.current;
      if (!s || s.mode !== "mouse") return;
      if (!s.moved) tapAt(e.currentTarget, e.clientX, e.clientY);
      g.current = null;
    },
    onMouseLeave() { if (g.current?.mode === "mouse") g.current = null; },
    onWheel(e) {
      const E = engineRef.current;
      if (!E) return;
      const [lx, ly] = local(e.currentTarget, e.clientX, e.clientY);
      E.zoomAtPoint(e.deltaY < 0 ? 1.15 : 0.87, lx, ly);
    },
  };
}
